/**
 * DST-safe wall clock <-> instant in an IANA zone. Extracted 2026-10 from
 * importParse.ts (excelDateCellToIso) so the spreadsheet importer and the
 * create-event Velo tool share ONE resolver. Do not change the resolution
 * rules without re-running importParse.excelDate.test.ts.
 * NOT the datetime-tz.ts resolver (that one is synced with
 * website-builder-frontend and resolves a spring-forward gap BACKWARD).
 *
 * Import-free apart from `isValidTimeZone` (and no `import.meta`): the Velo
 * tool core that uses it is bundled for Node by the velo-fix-work harness.
 */
import { isValidTimeZone } from "@/lib/utils/datetime-tz";

// One formatter per zone: `Intl.DateTimeFormat` construction is the expensive
// part, and a sheet calls this once per date cell per row. Same formatter
// options as the consumer's `excelWallClock` (export-timezone.ts) — the two
// sides of the round trip read the same clock.
const wallClockFormatters = new Map<string, Intl.DateTimeFormat>();
const wallClockFormatter = (tz: string): Intl.DateTimeFormat => {
  const hit = wallClockFormatters.get(tz);
  if (hit) return hit;
  const f = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  wallClockFormatters.set(tz, f);
  return f;
};

/**
 * The FULL wall date-time of instant `t` (whole seconds) in `tz`, packed as
 * `Date.UTC(<wall fields>)`. Day-aware on purpose: comparing minutes-of-day
 * alone cannot see a whole-day error, which is exactly what a ±720-minute
 * wrap produces for zones at or beyond ±12 h (Auckland +13, Chatham +13:45,
 * Kiritimati +14, Etc/GMT+12). Hour `'24'`: some ICU builds print midnight as
 * 24 under `hour12:false`; `hourCycle:'h23'` asks for 00, the guard covers the
 * builds that ignore it.
 */
export const wallOf = (t: number, tz: string): number => {
  const parts: Record<string, string> = {};
  for (const p of wallClockFormatter(tz).formatToParts(new Date(t))) {
    if (p.type !== "literal") parts[p.type] = p.value;
  }
  return Date.UTC(
    parseInt(parts.year ?? "0", 10),
    parseInt(parts.month ?? "1", 10) - 1,
    parseInt(parts.day ?? "1", 10),
    parts.hour === "24" ? 0 : parseInt(parts.hour ?? "0", 10),
    parseInt(parts.minute ?? "0", 10),
    parseInt(parts.second ?? "0", 10),
  );
};

/** Zone offset of `tz` at instant `t`, in ms (east positive). */
export const offsetAt = (t: number, tz: string): number => wallOf(t, tz) - t;

/** `isValidTimeZone` builds a DateTimeFormat per call; a zone that already has
 *  a cached wall-clock formatter is valid by construction, so the hot paths
 *  (wallClockToIso / isoToWallClock in loops) skip the rebuild. */
const isKnownValidZone = (tz: string): boolean =>
  wallClockFormatters.has(tz) || isValidTimeZone(tz);

/**
 * importParse's resolution, verbatim. `asUtc` = the wall fields framed as UTC;
 * candidate = `asUtc − offset(asUtc)`, verified by re-deriving its full wall
 * date-time; on a mismatch (DST change between guess and answer) recomputed
 * once with `offset(candidate)`. `gap` = no instant shows this wall time (the
 * LATER candidate wins — the post-jump instant).
 */
export function resolveWallClockDetailed(
  asUtc: number,
  tz: string,
): { at: number; gap: boolean } {
  const at = asUtc - offsetAt(asUtc, tz);
  if (wallOf(at, tz) !== asUtc) {
    const second = asUtc - offsetAt(at, tz);
    if (wallOf(second, tz) === asUtc) return { at: second, gap: false };
    return { at: Math.max(at, second), gap: true };
  }
  return { at, gap: false };
}

export const resolveWallClock = (asUtc: number, tz: string): number =>
  resolveWallClockDetailed(asUtc, tz).at;

const WALL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
const INSTANT_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|z|[+-]\d{2}:?\d{2})$/;
export const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
export const WALL_SHAPE_RE = WALL_RE;

/** Manual range check — V8's Date.parse ROLLS OVER "2027-02-30" (→ 03-02) and "T24:00". */
function fieldsValid(y: number, mo: number, d: number, h: number, mi: number, s: number): boolean {
  if (y < 1000 || mo < 1 || mo > 12 || d < 1 || h > 23 || mi > 59 || s > 59) return false;
  return new Date(Date.UTC(y, mo - 1, d)).getUTCDate() === d;
}

/** "YYYY-MM-DDTHH:mm[:ss]" → the wall fields packed as UTC ms (Date.UTC); null if not a real wall time. */
export function parseWallClock(s: string): number | null {
  const m = WALL_RE.exec(s);
  if (!m) return null;
  const y = +m[1], mo = +m[2], d = +m[3], h = +m[4], mi = +m[5], se = m[6] ? +m[6] : 0;
  if (!fieldsValid(y, mo, d, h, mi, se)) return null;
  return Date.UTC(y, mo - 1, d, h, mi, se);
}

/** ISO instant WITH zone ("…Z", "…+01:00", "…+0100"; seconds/fraction optional, fraction truncated to ms) → epoch ms; null otherwise.
 *  Computed manually (Date.UTC − offset), not via Date.parse: engine-independent (JSC rejects "+0100") and range-checked. Offset ≤ 18:00. */
export function parseZonedInstant(s: string): number | null {
  const m = INSTANT_RE.exec(s);
  if (!m) return null;
  const y = +m[1], mo = +m[2], d = +m[3], h = +m[4], mi = +m[5], se = m[6] ? +m[6] : 0;
  if (!fieldsValid(y, mo, d, h, mi, se)) return null;
  const ms = m[7] ? +m[7].slice(0, 3).padEnd(3, "0") : 0;
  let offsetMin = 0;
  const z = m[8];
  if (z !== "Z" && z !== "z") {
    const sign = z[0] === "-" ? -1 : 1;
    const digits = z.slice(1).replace(":", "");
    const oh = +digits.slice(0, 2), om = +digits.slice(2, 4);
    if (oh > 18 || om > 59 || (oh === 18 && om > 0)) return null;
    offsetMin = sign * (oh * 60 + om);
  }
  return Date.UTC(y, mo - 1, d, h, mi, se, ms) - offsetMin * 60_000;
}

export type WallClockResolution = {
  /** toISOString() shape */
  iso: string;
  adjustment: "gap" | "overlap" | null;
  /** zone offset at the resolved instant, in minutes (east +) */
  offsetMinutes: number;
};

/** Wall clock "YYYY-MM-DDTHH:mm[:ss]" in `tz` → the instant. Never throws; null for an invalid zone or a non-real wall time. */
export function wallClockToIso(wall: string, tz: string): WallClockResolution | null {
  if (!isKnownValidZone(tz)) return null;
  const asUtc = parseWallClock(wall);
  if (asUtc === null) return null;
  const { at, gap } = resolveWallClockDetailed(asUtc, tz);
  let adjustment: WallClockResolution["adjustment"] = gap ? "gap" : null;
  if (!gap) {
    // Overlap = a second instant shows the same wall time. ±6 h covers every real DST shift (incl. Lord Howe's 30 min).
    const H6 = 6 * 3_600_000;
    for (const o of new Set([offsetAt(at - H6, tz), offsetAt(at + H6, tz)])) {
      const alt = asUtc - o;
      if (alt !== at && wallOf(alt, tz) === asUtc) {
        adjustment = "overlap";
        break;
      }
    }
  }
  return {
    iso: new Date(at).toISOString(),
    adjustment,
    offsetMinutes: Math.round((wallOf(at, tz) - at) / 60_000),
  };
}

/** Instant → "YYYY-MM-DDTHH:mm" (":ss" appended only when non-zero, unless seconds:"never") in tz.
 *  Strings must carry Z/offset (parseZonedInstant) — a zone-less string returns null instead of being read in the browser frame. */
export function isoToWallClock(
  value: string | number | Date,
  tz: string,
  opts?: { seconds?: "auto" | "never" },
): string | null {
  const t =
    typeof value === "string"
      ? parseZonedInstant(value)
      : value instanceof Date
        ? value.getTime()
        : value;
  if (t === null || !Number.isFinite(t) || !isKnownValidZone(tz)) return null;
  const w = new Date(wallOf(t, tz)); // formatter has no fraction → whole seconds
  const iso = w.toISOString();
  return opts?.seconds === "never" || w.getUTCSeconds() === 0
    ? iso.slice(0, 16)
    : iso.slice(0, 19);
}

/** Strict: an instant string → toISOString() shape; null otherwise (never browser-frame parsing). */
export function toInstantIso(value: string): string | null {
  const t = parseZonedInstant(value.trim());
  return t === null ? null : new Date(t).toISOString();
}

/** Host normaliser: instant → ISO-Z; wall clock → ISO-Z resolved in tz; anything else → null. */
export function toEventInstantIso(value: string, tz: string): string | null {
  return toInstantIso(value) ?? wallClockToIso(value.trim(), tz)?.iso ?? null;
}

/** 60 → "+01:00", -240 → "-04:00", 630 → "+10:30". */
export function formatUtcOffset(minutes: number): string {
  const a = Math.abs(minutes);
  return `${minutes < 0 ? "-" : "+"}${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`;
}
